// 3) New widget: AdminStaffWidget.tsx
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Avatar, Box, Button, Chip, Divider, List, ListItem, ListItemAvatar, ListItemText, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { useMemo } from "react";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { WidgetComponentMaps } from "../common/WidgetComponentMaps";
import { colors } from "../ui/colors";
import { filledButton } from "../ui/styles/buttonStyles";
import { ManageAdmins } from "./ManageAdmins";
import { useGetBusinessOwner } from "./hooks/useGetBusinessOwner";
import { normalizeRole } from "./utils/admin-role.util";

export enum EUserOrgRole {
  Business = "Business",
  Admin = "Admin",
  VR = "Virtual Receptionist",
  Manager = "Manager",
}

export interface ICompanyMember {
  uid: string;
  avatar: string | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  alias?: string | null;
  displayName?: string | null;
  useAliasAsDisplayName?: boolean;

  UsersCompany?: {
    companyId: number;
    userRoleId: number;
    order: number;
  } | null;

  roles: Array<{
    name: string;
    type?: string;
    UserRoles?: {
      id: number;
    } | null;
  }>;
}

interface IUserRow {
  uid: string;
  name: string;
  email: string;
  avatar: string | null;
  role: EUserOrgRole;
}

const roleChipSx = (role: EUserOrgRole) => {
  const base = {
    height: 26,
    fontWeight: 900,
    borderRadius: "999px",
    textTransform: "none",
  };

  switch (role) {
    case EUserOrgRole.Business:
      return {
        ...base,
        bgcolor: "rgba(251,142,0,0.10)",
        color: colors.orange.base,
        border: `1px solid rgba(251,142,0,0.25)`,
      };

    case EUserOrgRole.Admin:
      return {
        ...base,
        bgcolor: "rgba(239,68,68,0.08)",
        color: "#b91c1c",
        border: "1px solid rgba(239,68,68,0.25)",
      };

    case EUserOrgRole.VR:
      return {
        ...base,
        bgcolor: "rgba(34,197,94,0.10)",
        color: "#15803d",
        border: "1px solid rgba(34,197,94,0.25)",
      };

    case EUserOrgRole.Manager:
      return {
        ...base,
        bgcolor: "rgba(33,150,243,0.10)",
        color: colors.info?.base || "#1976d2",
        border: "1px solid rgba(33,150,243,0.25)",
      };

    default:
      return {
        ...base,
        bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
        color: colors.gray.base,
        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
      };
  }
};

export interface IAdminStaffWidgetProps {
  swapyName?: string;
  sx?: any;
}

export const AdminStaffWidget = ({ swapyName = "adminStaffWidget", sx }: IAdminStaffWidgetProps) => {
  const { data, isLoading, isError } = useGetBusinessOwner();

  const ownerUid = useMemo(() => {
    const people: any[] = data?.data ?? [];
    if (people.length === 0) return null;

    const owner =
      people.find((p: any) => p.isOwner === true) ??
      people.find((p: any) => p.userRole?.role?.type === "business") ??
      [...people].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0] ??
      null;

    return owner?.userUid ?? null;
  }, [data]);

  const members = useMemo<IUserRow[]>(() => {
    const people: any[] = data?.data ?? [];

    return people
      .map((p: any) => {
        const user = p.user;

        const fullName = `${(user?.firstName || "").trim()} ${(user?.lastName || "").trim()}`.trim();

        const name =
          user?.displayName?.trim() || (user?.useAliasAsDisplayName && user?.alias?.trim() ? user.alias.trim() : fullName) || user?.email || "-";

        return {
          uid: p.userUid,
          name,
          email: user?.email || "-",
          avatar: user?.avatar || null,
          role: p.userUid === ownerUid ? EUserOrgRole.Business : normalizeRole(p.userRole?.role?.type),
          order: p.order ?? 0,
        };
      })
      .sort((a, b) => a.order - b.order);
  }, [data, ownerUid]);

  const isXs = useMediaQuery("(max-width:600px)");
  return (
    <WidgetComponentMaps
      swapyName={swapyName}
      title="Organization"
      description="View and manage organization members."
      icon={<ManageAccountsOutlinedIcon />}
      sxProp={{
        ...sx,
        // pr: 2,
        // height: { xs: "50vh", md: "20vh", lg: "20%" },
        // display: "flex",
        // flexDirection: "column",
      }}
      data={null}
      refResizeObserver={null}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <List sx={{ px: { xs: 0, md: 1 } }}>
          {isLoading ? (
            Array.from({ length: 3 })?.map((_, idx) => (
              <Box key={idx}>
                <ListItem
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    flexDirection: isXs ? "column" : "row",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="40%" height={20} />
                      <Skeleton width="60%" height={18} />
                    </Box>
                  </Box>
                  {isXs && <Skeleton width={80} height={24} sx={{ mt: 1.5, borderRadius: "999px" }} />}
                </ListItem>
                {idx < 2 ? <Divider sx={{ opacity: 0.6, mx: 1.5 }} /> : null}
              </Box>
            ))
          ) : isError ? (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography sx={{ fontWeight: 900, color: colors.gray.base }}>Could not load users.</Typography>
              <Typography sx={{ color: colors.gray[400], fontWeight: 600 }}>Please try again.</Typography>
            </Box>
          ) : (
            members?.map((m, idx) => (
              <Box key={m.uid}>
                <ListItem
                  secondaryAction={!isXs ? <Chip label={m.role} sx={roleChipSx(m.role)} size="small" /> : null}
                  sx={{
                    borderRadius: "14px",
                    px: { xs: 1.5, md: 2 },
                    py: 1.5,
                    flexDirection: isXs ? "column" : "row",
                    alignItems: isXs ? "flex-start" : "center",
                    "&:hover": { bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.03)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                    <ListItemAvatar sx={{ minWidth: "auto" }}>
                      <Avatar
                        src={m.avatar || undefined}
                        sx={{
                          bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                          color: colors.gray.base,
                          fontWeight: 900,
                          fontSize: 20,
                        }}
                      >
                        {m.role === EUserOrgRole.Business ? <ShieldOutlinedIcon fontSize="small" /> : <PersonOutlineIcon fontSize="small" />}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 900, fontSize: 15, lineHeight: 1.2, color: colors.gray.base }}>{m.name}</Typography>}
                      secondary={<Typography sx={{ color: colors.gray[400], fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}>{m.email}</Typography>}
                    />
                  </Box>

                  {isXs && (
                    <Box sx={{ mt: 1.5, pl: 7 }}>
                      <Chip label={m.role} sx={{ ...roleChipSx(m.role), height: 22, fontSize: 11 }} size="small" />
                    </Box>
                  )}
                </ListItem>

                {idx < members.length - 1 ? <Divider sx={{ opacity: 0.6, mx: 1.5 }} /> : null}
              </Box>
            ))
          )}
        </List>
      </Box>

      <Box
        data-swapy-no-drag
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          p: 1.5,
          mt: "auto",
          borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.05)",
          bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.8)",
          position: "relative",
          zIndex: 10,
        }}
      >
        <BaseModalComponent
          modalKey="Organization-ManageAdmins-Business"
          modalLabel="Organization"
          elementTrigger={
            <Button
              sx={{
                ...filledButton,
                cursor: "pointer",
                pointerEvents: "auto",
                "&:hover": {
                  ...filledButton["&:hover"],
                  cursor: "pointer",
                },
              }}
            >
              Organization
            </Button>
          }
        >
          <ManageAdmins />
        </BaseModalComponent>
      </Box>
    </WidgetComponentMaps>
  );
};
